import { useState, useEffect } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Trash2, Mail, Phone, FileText, Printer, HardHat } from "lucide-react";

const EMPTY = { name: "", email: "", phone: "", address: "", notes: "" };

export default function Clients() {
  const { t, lang } = useLang();
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [open, setOpen] = useState(false);
  const [statement, setStatement] = useState(null);

  const load = () => api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/clients", form);
      toast.success("Cliente agregado");
      setForm(EMPTY); setOpen(false); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/clients/${id}`); toast.success("Cliente eliminado"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const openStatement = async (c) => {
    try { const { data } = await api.get(`/clients/${c.id}/statement`); setStatement(data); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <h1 className="font-head text-2xl font-extrabold tracking-tight text-secondary">{t("dash_clients")}</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-sm bg-primary hover:bg-orange-600" data-testid="add-client-btn"><Plus className="mr-1 h-4 w-4" />{t("add")}</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{t("dash_clients")}</DialogTitle></DialogHeader>
            <form onSubmit={create} className="space-y-3" data-testid="client-form">
              <div><Label className="text-xs font-bold uppercase text-slate-500">{t("name")}</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} required className="mt-1 rounded-sm" data-testid="client-name-input" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs font-bold uppercase text-slate-500">{t("email")}</Label><Input value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-1 rounded-sm" data-testid="client-email-input" /></div>
                <div><Label className="text-xs font-bold uppercase text-slate-500">Teléfono</Label><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="mt-1 rounded-sm" data-testid="client-phone-input" /></div>
              </div>
              <div><Label className="text-xs font-bold uppercase text-slate-500">Dirección</Label><Input value={form.address} onChange={(e) => set("address", e.target.value)} className="mt-1 rounded-sm" /></div>
              <div><Label className="text-xs font-bold uppercase text-slate-500">Notas</Label><Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} className="mt-1 rounded-sm" /></div>
              <Button type="submit" className="w-full rounded-sm bg-primary hover:bg-orange-600" data-testid="client-save-btn">{t("save")}</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="clients-grid">
        {clients.map((c) => (
          <div key={c.id} className="rounded-sm border border-slate-200 bg-white p-5" data-testid={`client-card-${c.id}`}>
            <div className="flex items-start justify-between">
              <h3 className="font-head font-bold text-secondary">{c.name}</h3>
              <button onClick={() => remove(c.id)} className="text-slate-300 hover:text-destructive" data-testid={`client-delete-${c.id}`}><Trash2 className="h-4 w-4" /></button>
            </div>
            {c.email && <p className="mt-2 flex items-center gap-1.5 text-sm text-slate-500"><Mail className="h-3.5 w-3.5" /> {c.email}</p>}
            {c.phone && <p className="mt-1 flex items-center gap-1.5 text-sm text-slate-500"><Phone className="h-3.5 w-3.5" /> {c.phone}</p>}
            {c.notes && <p className="mt-2 text-sm text-slate-600">{c.notes}</p>}
            <Button variant="outline" size="sm" onClick={() => openStatement(c)} className="mt-3 w-full rounded-sm" data-testid={`client-statement-${c.id}`}><FileText className="mr-1 h-3.5 w-3.5" />{lang === "es" ? "Estado de cuenta" : "Statement"}</Button>
          </div>
        ))}
        {clients.length === 0 && <p className="col-span-full py-10 text-center text-slate-400">—</p>}
      </div>

      <Dialog open={!!statement} onOpenChange={(o) => !o && setStatement(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle className="no-print">{lang === "es" ? "Estado de cuenta" : "Account statement"}</DialogTitle></DialogHeader>
          {statement && (
            <div className="statement-print" data-testid="client-statement">
              <div className="flex items-center justify-between border-b border-slate-200 pb-4">
                <div className="flex items-center gap-3">
                  {statement.business.logo ? <img src={statement.business.logo} alt="logo" className="h-12 w-12 object-contain" /> : <span className="flex h-12 w-12 items-center justify-center rounded-sm bg-primary text-white"><HardHat className="h-6 w-6" /></span>}
                  <div><p className="font-head text-lg font-bold text-secondary">{statement.business.name}</p><p className="text-xs text-slate-400">{lang === "es" ? "Estado de cuenta" : "Account statement"}</p></div>
                </div>
                <div className="text-right text-sm"><p className="font-semibold text-secondary">{statement.client.name}</p>{statement.client.email && <p className="text-xs text-slate-400">{statement.client.email}</p>}</div>
              </div>
              <table className="mt-4 w-full text-sm">
                <thead className="border-b border-slate-200"><tr className="text-left text-xs font-bold uppercase tracking-wider text-slate-400"><th className="py-2">#</th><th className="py-2">{lang === "es" ? "Fecha" : "Date"}</th><th className="py-2">{lang === "es" ? "Estado" : "Status"}</th><th className="py-2 text-right">{lang === "es" ? "Monto" : "Amount"}</th></tr></thead>
                <tbody>
                  {statement.invoices.map((i) => (
                    <tr key={i.id} className="border-b border-slate-100" data-testid={`statement-inv-${i.id}`}>
                      <td className="py-2 font-semibold text-secondary">{i.number}</td>
                      <td className="py-2 text-slate-500">{(i.created_at || "").slice(0, 10)}</td>
                      <td className="py-2"><span className={`rounded-sm px-1.5 py-0.5 text-[10px] font-bold uppercase ${i.status === "paid" ? "bg-accent/10 text-accent" : "bg-primary/10 text-primary"}`}>{i.status === "paid" ? (lang === "es" ? "Pagada" : "Paid") : (lang === "es" ? "Pendiente" : "Pending")}</span></td>
                      <td className="py-2 text-right font-semibold text-secondary">${i.total.toFixed(2)}</td>
                    </tr>
                  ))}
                  {statement.invoices.length === 0 && <tr><td colSpan={4} className="py-6 text-center text-slate-400">{lang === "es" ? "Sin facturas." : "No invoices."}</td></tr>}
                </tbody>
              </table>
              <div className="mt-4 space-y-1 border-t border-slate-200 pt-3 text-sm">
                <div className="flex justify-between text-slate-500"><span>{lang === "es" ? "Total facturado" : "Total invoiced"}</span><span data-testid="statement-invoiced">${statement.total_invoiced.toFixed(2)}</span></div>
                <div className="flex justify-between text-accent"><span>{lang === "es" ? "Total pagado" : "Total paid"}</span><span data-testid="statement-paid">${statement.total_paid.toFixed(2)}</span></div>
                <div className={`flex justify-between font-head text-lg font-extrabold ${statement.balance > 0 ? "text-destructive" : "text-accent"}`}><span>{lang === "es" ? "Saldo pendiente" : "Balance due"}</span><span data-testid="statement-balance">${statement.balance.toFixed(2)}</span></div>
              </div>
              <Button onClick={() => window.print()} className="no-print mt-6 w-full rounded-sm bg-secondary hover:bg-slate-700" data-testid="statement-print-btn"><Printer className="mr-2 h-4 w-4" />{lang === "es" ? "Imprimir / PDF" : "Print / PDF"}</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
